import React, { useState, useEffect } from "react";

import { IconRefreshCw } from "@frontend/common";

import { usePatentPrimaryCategoryFilterQuery } from "@api/patent/patentPrimaryCategory/usePatentPrimaryCategory";
import BorderBox from "@atoms/BorderBox/BorderBox";
import FilterTag from "@atoms/FilterTag/FilterTag";
import IconCheck from "@images/common/icon-filter-check-white.svg";
import XMarkIcon from "@images/pressList/icon-x-mark-red.svg";
import { ClassificationList } from "@PressListContainer/classificationInfo.constants";
import { usePatentListFilterState } from "@stores/patentList";

import * as S from "./PatentPrimaryCategoryFilter.style";

interface Props {
  closeModal: () => void;
}

const PatentPrimaryCategoryFilter = ({ closeModal }: Props) => {
  const [patentListFilter, setPatentListFilter] = usePatentListFilterState();

  const { primaryCategory } = patentListFilter;

  const { data: primaryCategoryFilterData } =
    usePatentPrimaryCategoryFilterQuery();

  const [primaryCategoryList, setPrimaryCategoryList] = useState<
    ClassificationList[]
  >(primaryCategoryFilterData?.ai || []);
  const [isAllChecked, setIsAllChecked] = useState(false);

  const changePrimaryCategoryTargetList = (
    event: React.ChangeEvent<HTMLInputElement>,
    list: ClassificationList[],
  ) => {
    return list.map((item) =>
      item.code === event.currentTarget.value
        ? { ...item, isCheck: !item.isCheck }
        : item,
    );
  };

  const handlePrimaryCategoryChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const targetElement = event.currentTarget;

    if (targetElement.value === "all") {
      setIsAllChecked(targetElement.checked);
      setPrimaryCategoryList(
        primaryCategoryList.map((c) => ({
          ...c,
          isCheck: targetElement.checked,
        })),
      );
    } else {
      setPrimaryCategoryList(
        changePrimaryCategoryTargetList(event, primaryCategoryList),
      );
    }
  };

  const handleConfirm = () => {
    const selectedPrimaryCategory = primaryCategoryList
      .filter((c) => c.isCheck)
      .map((c) => c.code);

    setPatentListFilter({
      ...patentListFilter,
      primaryCategory: selectedPrimaryCategory,
      page: 1,
    });
    closeModal();
  };

  const handleReset = () => {
    setIsAllChecked(false);
    setPrimaryCategoryList(
      (primaryCategoryFilterData?.ai || []).map((c) => ({
        ...c,
        isCheck: false,
      })),
    );
  };

  useEffect(() => {
    setPrimaryCategoryList(
      (primaryCategoryFilterData?.ai || []).map((c) => ({
        ...c,
        isCheck: primaryCategory.includes(c.code),
      })),
    );
  }, [primaryCategory, primaryCategoryFilterData?.ai]);

  useEffect(() => {
    const totalItems = primaryCategoryList.length;
    const checkedItems = primaryCategoryList.filter(
      (item) => item.isCheck,
    ).length;

    setIsAllChecked(totalItems > 0 && totalItems === checkedItems);
  }, [primaryCategoryList]);

  const totalCheckedPrimaryCategory = primaryCategoryList.filter(
    (item) => item.isCheck,
  ).length;

  const confirmButtonText =
    totalCheckedPrimaryCategory > 1
      ? "Apply Primary Category Filters"
      : "Apply Primary Category Filter";

  return (
    <S.StatusFilterWrapper>
      <BorderBox padding="16">
        <S.StatusFilterContentWrapper>
          <S.StatusFilterTitleRow>
            <S.FilterResetButton
              onClick={handleReset}
              disabled={totalCheckedPrimaryCategory < 1}
            >
              {totalCheckedPrimaryCategory < 1 ? (
                <IconRefreshCw width={20} height={20} color="#a5a5a5" />
              ) : (
                <IconRefreshCw width={20} height={20} color="#515C6C" />
              )}
              <S.FilterResetButtonText>Reset</S.FilterResetButtonText>
            </S.FilterResetButton>
            <S.PrimaryCategoryCloseButton type="button" onClick={closeModal}>
              <S.PrimaryCategoryXMark>
                <img src={XMarkIcon} alt="close filter" />
              </S.PrimaryCategoryXMark>
              <S.PrimaryCategoryCloseText>Close</S.PrimaryCategoryCloseText>
            </S.PrimaryCategoryCloseButton>
          </S.StatusFilterTitleRow>
          <S.StatusFilterTitleWrapper>
            <S.StatusFilterTitle>Primary Category</S.StatusFilterTitle>
            <FilterTag
              name="primaryCategory"
              value="all"
              onChange={handlePrimaryCategoryChange}
              checked={isAllChecked}
              isAll
            >
              Select All
            </FilterTag>
          </S.StatusFilterTitleWrapper>
          <S.StatusFilterRow>
            <S.StatusFilterFieldset>
              {primaryCategoryList.map((item) => (
                <FilterTag
                  key={`primaryCategory-${item.code}`}
                  tagType="checkbox"
                  onChange={handlePrimaryCategoryChange}
                  name="primaryCategory"
                  value={item.code}
                  checked={item.isCheck}
                  filterType="regulatoryClassification"
                  topicSelected
                >
                  {item.name}
                </FilterTag>
              ))}
            </S.StatusFilterFieldset>
          </S.StatusFilterRow>
          <S.ConfirmButtonWrapper>
            <S.ConfirmButton onClick={handleConfirm}>
              <img src={IconCheck} alt="filter check icon" />
              <S.ConfirmButtonText>{confirmButtonText}</S.ConfirmButtonText>
            </S.ConfirmButton>
          </S.ConfirmButtonWrapper>
        </S.StatusFilterContentWrapper>
      </BorderBox>
    </S.StatusFilterWrapper>
  );
};

export default PatentPrimaryCategoryFilter;
